// ** react imports
import React, {
  useCallback,
  useRef,
  useState,
  useMemo,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';

// ** react route imports
import { useParams } from 'react-router-dom';

// ** redux imports
import {
  useUploadTaxFileDocument,
  useDeleteDocument,
  useGetTaxFileDocuments,
  useGetDocumentDownloadUrl,
} from '../../../../hooks/apis/useDocuments';

// ** components imports
import Loading from '../../../common/Loading';

const isSupportedExtension = fileName => {
  const lowerFileName = fileName.toLowerCase();
  const supportedExtensions = [
    '.pdf',
    '.doc',
    '.docx',
    '.xlsx',
    '.csv',
    '.ppt',
    '.pptx',
    '.odt',
    '.ods',
    '.odp',
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.bmp',
    '.tiff',
    '.tif',
    '.heic',
    '.heif',
    '.webp',
    '.raw',
    '.cr2',
    '.nef',
    '.arw',
  ];

  return supportedExtensions.some(ext => lowerFileName.endsWith(ext));
};

const FileItem = ({
  data,
  isLoading,
  disabled,
  handleRemoveFile,
  handleDownloadFile,
}) => {
  return (
    <div className="flex items-center justify-between space-x-3 px-3 py-2 rounded-lg border  bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-600">
      <div className="w-10/12">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[100px] xs:max-w-[120px] sm:max-w-xs">
          {data.originalName}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {(data.originalSize / 1024 / 1024).toFixed(2)} MB
        </p>
      </div>
      <div className="">
        {isLoading ? (
          <Loading size="small" />
        ) : disabled && data.type === 'pre-populated' ? (
          <button
            type="button"
            onClick={() => handleDownloadFile(data)}
            className="text-gray-400 hover:text-blue-500 dark:text-gray-500 dark:hover:text-blue-400 cursor-pointer"
            aria-label="Download file"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
          </button>
        ) : !disabled ? (
          <button
            type="button"
            onClick={() => handleRemoveFile(data)}
            className="text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 cursor-pointer"
            aria-label="Remove file"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        ) : null}
      </div>
    </div>
  );
};

const FileUploader = ({
  data,
  name,
  control,
  onFileUpload,
  questionId,
  maxSize = 25 * 1024 * 1024,
  acceptedTypes = [],
  maxFiles = 4,
  multiple = true,
  disabled = false,
  className = '',
  ...props
}) => {
  // states
  const [files, setFiles] = useState([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [fileLoadingStates, setFileLoadingStates] = useState({});

  // refs
  const fileInputRef = useRef(null);

  // custom hooks
  const { taxFileId } = useParams();
  const { uploadTaxFileDocument, isUploadTaxFileDocumentLoading } =
    useUploadTaxFileDocument();
  const { deleteDocument } = useDeleteDocument();
  const {
    data: taxFileDocuments,
    getTaxFileDocuments,
    isGetTaxFileDocumentsFetching,
    isGetTaxFileDocumentsLoading,
  } = useGetTaxFileDocuments();
  const { getDocumentDownloadUrl } = useGetDocumentDownloadUrl();

  // hooks
  const { value, onChange } = useMemo(() => {
    if (control) {
      return {
        value: control._f?.value || [],
        onChange: control._f?.onChange || (() => {}),
      };
    }
    return {
      value: files,
      onChange: setFiles,
    };
  }, [control, files]);

  useEffect(() => {
    if (taxFileId && data.questionId && !hasFetched) {
      getTaxFileDocuments({ taxFileId, questionId: data.questionId });
      setHasFetched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxFileId, data.questionId, hasFetched]);

  useEffect(() => {
    if (taxFileDocuments !== undefined && taxFileDocuments.documents) {
      const prePopulatedFiles = taxFileDocuments.documents.map(doc => ({
        documentId: doc.documentId,
        originalName: doc.originalName,
        originalSize: doc.originalSize,
        type: 'pre-populated',
        uploadedAt: doc.uploadedAt,
        status: doc.status,
        documentType: doc.documentType,
        processedSize: doc.processedSize,
      }));

      if (JSON.stringify(value) !== JSON.stringify(prePopulatedFiles)) {
        onChange(prePopulatedFiles);
        onFileUpload(prePopulatedFiles);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxFileDocuments]);

  // handlers
  const handleFileSelect = useCallback(
    async selectedFiles => {
      if (disabled || !selectedFiles || selectedFiles.length === 0) return;

      const filesArray = Array.from(selectedFiles);
      const validFiles = [];
      const invalidFiles = [];

      if (value && value.length + filesArray.length > maxFiles) {
        return;
      }

      if (onFileUpload) {
        onFileUpload(validFiles);
      }

      const validateFile = file => {
        if (file.originalSize > maxSize) {
          return {
            valid: false,
            error: `File size exceeds maximum limit of ${(maxSize / (1024 * 1024)).toFixed(2)} MB`,
          };
        }

        if (acceptedTypes.length > 0) {
          if (acceptedTypes.includes(file.type)) {
            return { valid: true, error: null };
          }

          if (!isSupportedExtension(file.originalName)) {
            return {
              valid: false,
              error: `File type not supported. Allowed extensions: .pdf, .doc, .docx, .xls, .xlsx, .csv, .ppt, .pptx, .odt, .ods, .odp, .jpg, .jpeg, .png, .gif, .bmp, .tiff, .tif, .heic, .heif, .webp, .raw, .cr2, .nef, .arw`,
            };
          }
        }

        return { valid: true, error: null };
      };

      for (const file of filesArray) {
        const validation = validateFile(file);
        if (validation.valid) {
          validFiles.push(file);
        } else {
          invalidFiles.push({ file, error: validation.error });
        }
      }

      if (invalidFiles.length > 0) {
        return;
      }

      if (validFiles.length > 0) {
        const formData = new FormData();
        validFiles.forEach((file, index) => {
          formData.append('documents', file);
        });

        if (data.questionId) {
          formData.append('questionId', data.questionId);
        }

        const newFilesWithMetadata = validFiles.map(file => ({
          originalName: file.name,
          originalSize: file.size,
          type: 'pre-populated',
          documentId:
            file.documentId ||
            `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        }));
        const newValue = [...(value || []), ...newFilesWithMetadata];
        onChange(newValue);
        if (onFileUpload) {
          onFileUpload(newValue);
        }

        try {
          const response = await uploadTaxFileDocument({ taxFileId, formData });
          if (response.data.success) {
            const documents = response.data.data.documents;
            if (documents.length > 0) {
              const prePopulatedFiles = documents.map(doc => ({
                documentId: doc.documentId,
                originalName: doc.originalName,
                originalSize: doc.originalSize,
                type: 'pre-populated',
                uploadedAt: doc.uploadedAt,
                status: doc.status,
                documentType: doc.documentType,
                processedSize: doc.processedSize,
              }));

              onChange(prePopulatedFiles);
              if (onFileUpload) {
                onFileUpload(prePopulatedFiles);
              }
            }
          }
        } catch (error) {
          return error;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      disabled,
      value,
      maxFiles,
      maxSize,
      acceptedTypes,
      data.questionId,
      uploadTaxFileDocument,
      taxFileId,
    ]
  );

  const handleDragOver = useCallback(e => {
    e.preventDefault();
  }, []);

  const handleDragLeave = useCallback(e => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    e => {
      e.preventDefault();

      const droppedFiles = e.dataTransfer.files;
      if (droppedFiles.length > 0) {
        handleFileSelect(droppedFiles);
      }
    },
    [handleFileSelect]
  );

  const handleFileInputChange = useCallback(
    e => {
      const selectedFiles = e.target.files;
      if (selectedFiles && selectedFiles.length > 0) {
        handleFileSelect(selectedFiles);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [handleFileSelect]
  );

  const removeFile = useCallback(
    async fileToRemove => {
      if (value && Array.isArray(value)) {
        if (fileToRemove.documentId) {
          try {
            const response = await deleteDocument(fileToRemove.documentId);
            if (response.data.success) {
              const documents = response.data.data.documents;
              if (documents.length > 0) {
                const updatedDocuments = documents.map(doc => ({
                  documentId: doc.documentId,
                  originalName: doc.originalName,
                  originalSize: doc.originalSize,
                  type: 'pre-populated',
                  uploadedAt: doc.uploadedAt,
                  status: doc.status,
                  documentType: doc.documentType,
                  processedSize: doc.processedSize,
                }));

                if (
                  JSON.stringify(value) !== JSON.stringify(updatedDocuments)
                ) {
                  onChange(updatedDocuments);
                  onFileUpload(updatedDocuments);
                }
              }
            }
          } catch (error) {
            return error;
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteDocument]
  );

  const triggerFileInput = useCallback(() => {
    if (fileInputRef.current && !disabled) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  const downloadFile = async file => {
    // Generate a unique key for loading state
    const fileKey = file.documentId;
    setFileLoadingStates(prev => ({
      ...prev,
      [fileKey]: true,
    }));

    try {
      if (file.type === 'pre-populated' && file.documentId) {
        const result = await getDocumentDownloadUrl(file.documentId);
        if (result.data) {
          const downloadData = result.data.data;
          const link = document.createElement('a');
          link.href = downloadData.downloadURL;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          if (downloadData.downloadURL.startsWith('blob:')) {
            URL.revokeObjectURL(downloadData.downloadURL);
          }
        }
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    } finally {
      setFileLoadingStates(prev => {
        const newState = { ...prev };
        delete newState[fileKey];
        return newState;
      });
    }
  };

  // ** render helpers
  const renderFileList = () => {
    return (
      <div className="mt-4">
        {isGetTaxFileDocumentsFetching || isGetTaxFileDocumentsLoading ? (
          <div className="backdrop-blur-sm z-40 flex items-center justify-center rounded-xl">
            <div className="flex items-center justify-center gap-3 text-gray-600 dark:text-gray-300">
              <Loading size="medium" />
            </div>
          </div>
        ) : (
          <>
            {!disabled && value.length < maxFiles && (
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Selected Files ({value.length})
                </h4>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-60 overflow-y-auto">
              {value.map((file, index) => (
                <div key={file.documentId || index}>
                  <FileItem
                    data={file}
                    disabled={disabled}
                    handleRemoveFile={removeFile}
                    handleDownloadFile={downloadFile}
                    isLoading={fileLoadingStates[file.documentId] || false}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div {...props}>
      {!disabled &&
        (value.length !== maxFiles || isUploadTaxFileDocumentLoading) && (
          <div
            className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={triggerFileInput}
            aria-disabled={disabled}
          >
            {!isUploadTaxFileDocumentLoading ? (
              <div className="">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  multiple={multiple}
                  onChange={handleFileInputChange}
                  accept={
                    acceptedTypes.length > 0
                      ? acceptedTypes.join(',')
                      : undefined
                  }
                  disabled={disabled}
                />
                <div className="flex flex-col items-center justify-center space-y-2">
                  <svg
                    className="w-6 h-6 text-gray-400 dark:text-gray-500"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div>
                    <p className="text-base font-medium text-gray-900 dark:text-gray-100">
                      Click to select files
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {multiple
                        ? 'or drag and drop up to '
                        : 'or drag and drop '}{' '}
                      {maxFiles} file{maxFiles > 1 ? 's' : ''}
                      {acceptedTypes.length > 0
                        ? ` (${acceptedTypes.join(', ')})`
                        : ''}{' '}
                      Max file size: {(maxSize / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center p-5.5">
                <Loading size="medium" />
              </div>
            )}
          </div>
        )}

      {renderFileList()}
    </div>
  );
};

FileUploader.propTypes = {
  data: PropTypes.object,
  name: PropTypes.string,
  control: PropTypes.object,
  onFileUpload: PropTypes.func,
  questionId: PropTypes.string,
  maxSize: PropTypes.number,
  acceptedTypes: PropTypes.arrayOf(PropTypes.string),
  maxFiles: PropTypes.number,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

export default FileUploader;
